import { useQuery } from "@tanstack/react-query";
import clsx from "clsx";
import { FlaskConical } from "lucide-react";
import { type ReactNode, useEffect } from "react";

import { api } from "../api/client";
import type { PromotionSummary } from "../api/types";
import { Motion } from "../components/presence";
import { t, useLang } from "../i18n/i18n";
import { AnnouncementHost } from "./AnnouncementBanner";
import { LicenceMessageHost } from "../licence/LicenceMessageBanner";
import { documentTitle, isPreprod, useEnvironmentValue } from "./environment";
import { formatBuild, formatParis, PROMOTION_KEY } from "./home/promotion";
import { CLIENT_BUILD } from "./versionCheck";

/** Bandeau « PRÉPRODUCTION » (registre F35) : en haut de tous les écrans de la préproduction (activation, accueil,
 *  espace de travail), jamais en production. Posé au-dessus de l'application dans `PreprodFrame` : il décale tout de
 *  sa hauteur (28 px), si bien que le logo garde sa place entre l'application et l'accueil. Les voiles plein écran
 *  (fenêtres, aide, prise en main, palette, confirmations) commencent sous lui (`--gx-strip-h`, index.css, posé par
 *  `data-gx-strip` sur <html>) : il ne masque jamais leur en-tête ni leur bouton de fermeture, et reste visible.
 *  Orange d'avertissement, texte blanc, z-85 (sous les menus, z-100). Entre en fondu. La date de la copie des données
 *  vient du résumé de la promotion, lu par l'encadré de l'accueil (licence exigée) : absente avant. */

export function PreprodStrip({ build, copiedAt }: { build: string | null; copiedAt: string | null }) {
  return (
    <Motion
      origin="fade"
      role="status"
      data-preprod-strip
      className="relative z-[85] flex h-7 shrink-0 items-center gap-2 overflow-hidden bg-warn px-3 text-meta whitespace-nowrap text-surface"
    >
      <FlaskConical size={14} className="shrink-0" aria-hidden />
      <span className="shrink-0 font-semibold tracking-[0.06em]">{t("PRÉPRODUCTION")}</span>
      <Part className="max-md:hidden">{t("Environnement d'essai : ce qui est fait ici ne touche pas la production")}</Part>
      <Part className="max-sm:hidden">{t("Version du {date}", { date: formatBuild(build ?? CLIENT_BUILD) })}</Part>
      {copiedAt && <Part className="max-lg:hidden">{t("Données copiées de la production le {date}", { date: formatParis(copiedAt) })}</Part>}
    </Motion>
  );
}

/** Morceau du bandeau, précédé d'un point ; coupé proprement s'il ne tient pas. */
function Part({ className, children }: { className?: string; children: ReactNode }) {
  return (
    <span className={clsx("min-w-0 truncate", className)}>
      <span aria-hidden className="mr-2 opacity-60">
        ·
      </span>
      {children}
    </span>
  );
}

/** Bandeau de la préproduction lu dans l'environnement (rien en production ni en développement) ; titre de l'onglet
 *  dans la langue de l'interface. */
export function PreprodStripHost() {
  const preprod = useEnvironmentValue(isPreprod);
  const build = useEnvironmentValue((s) => s.build);
  const language = useLang();
  // Résumé déjà lu par l'encadré de l'accueil : jamais demandé ici (l'écran d'activation n'a pas de licence).
  const summary = useQuery<PromotionSummary>({
    queryKey: PROMOTION_KEY,
    queryFn: api.preprod.promotion,
    enabled: false,
  });
  useEffect(() => {
    if (typeof document !== "undefined") document.title = documentTitle(preprod ? "preprod" : "production", t("Préproduction"));
  }, [preprod, language]);
  // Voiles plein écran décalés de la hauteur du bandeau (index.css) ; rien en production.
  useEffect(() => {
    if (typeof document === "undefined") return;
    document.documentElement.toggleAttribute("data-gx-strip", preprod);
    return () => document.documentElement.removeAttribute("data-gx-strip");
  }, [preprod]);
  if (!preprod) return null;
  const preprodInfo = summary.data?.preprod;
  return <PreprodStrip build={preprodInfo?.build ?? build} copiedAt={preprodInfo?.data_copied_at ?? null} />;
}

/** Cadre de l'application : le bandeau (préproduction seulement), l'annonce d'une maintenance (registre F40, dans
 *  tous les environnements, s'il y en a une), puis l'application dans la place restante. La structure ne change pas
 *  d'un environnement à l'autre : rien n'est remonté quand l'environnement est connu. */
export function PreprodFrame({ children }: { children: ReactNode }) {
  return (
    <div className="flex h-full flex-col">
      <PreprodStripHost />
      <AnnouncementHost />
      {/* Message de BELLICOM à la licence du poste (administration des licences), s'il y en a un. */}
      <LicenceMessageHost />
      <div className="relative min-h-0 flex-1">{children}</div>
    </div>
  );
}
